import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateLiftSelected,
  updateBankSelected,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UTILS
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import { stringToUuidT } from "../../../utils/uuid";

function MonitoringElevatorFullViewName({
  lift,
  bank,
  serverId,
  channelId,
  enableActions,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
  enableActions: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [liftId, setLiftId] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissions } = useSelector((state: RootState) => state.user);
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    if (hasFaults) {
      return "FAULTS";
    }
    if (validatePermission("monitor-control-panel-car-calls", permissions)) {
      return "CARCALLS";
    }
    if (validatePermission("monitor-control-panel-hall-calls", permissions)) {
      return "HALLCALLS";
    }
    if (validatePermission("monitor-control-panel-actions", permissions)) {
      return "ACTIONS";
    }
    if (validatePermission("monitor-control-panel-faults", permissions)) {
      return "FAULTS";
    }
    if (validatePermission("monitor-control-panel-status", permissions)) {
      return "STATUS";
    }
    return "CARCALLS";
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const onClickOpenModalElevator = (e: any) => {
    if (enableActions) {
      getDrawerPlacement(e);
      dispatch(
        updateModalControlPanelTab({
          liftType: "ELEVATOR",
          tab: getTabKey(),
        })
      );
      dispatch(updateLiftSelected(lift));
      dispatch(updateBankSelected(bank));
      dispatch(updateChannelId(channelId));
      dispatch(updateServerId(serverId));
      dispatch(updateOpenModalControlPanelLift(true));
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (lift && lift.id && lift.id !== "") {
      setLiftId(lift.id);
    }
  }, [lift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className="mt-2"
      role="button"
      tabIndex={0}
      onKeyDown={emptyFunctionDeclaration}
      onClick={onClickOpenModalElevator}
      style={{ fontSize: "9px", fontWeight: "bolder" }}
    >
      <div>
        <Tooltip placement="top" title={lift.name}>
          <span className={hasFaults ? "generalStyles__error" : ""}>
            {lift.name.toUpperCase().substring(0, 6)}
          </span>
        </Tooltip>
      </div>
      {!hasFaults && !socketMessageLift.serviceMode && !previewFaultServiceMode && (
        <div style={{ height: "10px" }} />
      )}
      {hasFaults && (
        <div>
          <Tag
            style={{
              marginTop: "5px",
              fontSize: "10px",
              width: "100%",
            }}
            color="red"
          >
            FAULT
          </Tag>
        </div>
      )}
      {(socketMessageLift.serviceMode || previewFaultServiceMode) &&
        (() => {
          if (previewFaultServiceMode && !socketMessageLift.serviceMode) {
            return (
              <div>
                <Tooltip placement="top" title="Energy Saving">
                  <Tag
                    style={{
                      marginTop: "5px",
                      width: "100%",
                      fontSize: "10px",
                    }}
                    color="blue"
                  >
                    ENER
                  </Tag>
                </Tooltip>
              </div>
            );
          }
          const display = getServiceModeDisplay(
            socketMessageLift.serviceMode?.messageType ?? null,
            socketMessageLift.serviceMode?.message?.message ?? null
          );
          if (!display) return null;
          return (
            <div>
              <Tooltip placement="top" title={display.displayName}>
                <Tag
                  style={{
                    marginTop: "5px",
                    width: "100%",
                    fontSize: "10px",
                  }}
                  color={display.color}
                >
                  {display.abbreviation.substring(0, 4)}
                </Tag>
              </Tooltip>
            </div>
          );
        })()}
    </div>
  );
}

export default React.memo(MonitoringElevatorFullViewName);
